// related component

import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { LockIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { signal } from '@preact/signals-core';
import { css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { html } from 'lit/static-html.js';

import type {
  CellRenderProps,
  DataViewCellLifeCycle,
} from '../../../core/property/index.js';
import { renderUniLit } from '../../../core/utils/uni-component/uni-component.js';
import type { Property } from '../../../core/view-manager/property.js';
import type { KanbanViewSelection } from '../selection';
import type { KanbanViewUILogic } from './kanban-view-ui-logic.js';

const styles = css`
  affine-data-view-kanban-cell {
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    padding: 4px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 20px;
    border: 1px solid transparent;
    box-sizing: border-box;
  }

  affine-data-view-kanban-cell:hover {
    background-color: var(--streamlain-hover-color);
  }

  affine-data-view-kanban-cell .icon-stack {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: start;
    margin-right: 12px;
    height: var(--data-view-cell-text-line-height);
    flex-shrink: 0;
  }

  affine-data-view-kanban-cell .icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  affine-data-view-kanban-cell .icon svg {
    width: 16px;
    height: 16px;
    fill: var(--streamlain-icon-color);
    color: var(--streamlain-icon-color);
  }

  /* Readonly lock overlays the bottom-right of the property icon
     instead of sitting next to the value in the text row. Saves a
     column of horizontal space on narrow cards and keeps the content
     row vertically aligned with non-readonly cells. Small white
     halo via background + ring so the glyph reads on any card tint. */
  affine-data-view-kanban-cell .icon-readonly-overlay {
    position: absolute;
    right: -4px;
    bottom: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1px;
    border-radius: 50%;
    background-color: var(--streamlain-background-primary-color);
    box-shadow: 0 0 0 1px var(--streamlain-background-primary-color);
    color: var(--streamlain-icon-color);
    opacity: 0.7;
    pointer-events: none;
  }

  affine-data-view-kanban-cell .icon-readonly-overlay svg {
    width: 9px;
    height: 9px;
    display: block;
    fill: currentColor;
    color: currentColor;
  }

  .kanban-cell {
    flex: 1 1 0;
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .kanban-cell-frame {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .kanban-cell-label {
    color: var(--streamlain-text-secondary-color);
    font-size: 11px;
    line-height: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  affine-data-view-kanban-cell .kanban-cell > *:first-child {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
`;

export class KanbanCell extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = styles;

  private readonly _cell = signal<DataViewCellLifeCycle>();

  selectCurrentCell = (editing: boolean) => {
    const selectionView = this.kanbanViewLogic.selectionController;
    if (selectionView) {
      const selection = selectionView.selection;
      if (selection && this.isSelected(selection) && editing) {
        selectionView.selection = {
          selectionType: 'cell',
          groupKey: this.groupKey,
          rowKey: this.rowKey,
          cardId: this.cardId,
          columnId: this.column.id,
          isEditing: true,
        };
      } else {
        selectionView.selection = {
          selectionType: 'cell',
          groupKey: this.groupKey,
          rowKey: this.rowKey,
          cardId: this.cardId,
          columnId: this.column.id,
          isEditing: false,
        };
      }
    }
  };

  get cell(): DataViewCellLifeCycle | undefined {
    return this._cell.value;
  }

  get selection() {
    return this.kanbanViewLogic.selectionController;
  }

  override connectedCallback() {
    super.connectedCallback();
    this._disposables.addFromEvent(this, 'click', e => {
      // Modifier clicks belong to the shared RowSelectionController
      // (range / toggle) — the card handler in capture-phase owns the
      // modifier path. Cell focus must stay out of those gestures so
      // it doesn't flip the view selection to `{type:cell}` mid-range.
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        return;
      }
      e.stopPropagation();
      const selectionElement = this.kanbanViewLogic.selectionController;
      if (!selectionElement) return;
      if (this.isEditing$.value) return;
      if (this.view.readonly$.value || this.column.valueReadonly$.value) {
        this.selectCurrentCell(false);
        return;
      }

      // When the host card is already the single-row selection, the
      // card-handler has let this click through — the user's INTENT
      // is then "edit this field", not "focus it and wait for a
      // second click". Collapsing the focus-then-edit dance to one
      // click matches the table view's muscle memory (dblclick there,
      // but the kanban card's own "first click selects" already acts
      // as the first click of that sequence).
      const rowIds = this.kanbanViewLogic.rowSelection.selectedIds$.value;
      const cardIsLoneSelection = rowIds.size === 1 && rowIds.has(this.cardId);
      if (cardIsLoneSelection) {
        selectionElement.selection = {
          selectionType: 'cell',
          groupKey: this.groupKey,
          rowKey: this.rowKey,
          cardId: this.cardId,
          columnId: this.column.id,
          isEditing: true,
        };
        return;
      }

      this.selectCurrentCell(true);
    });

    // Dblclick safety net: mirrors table/gantt so a user who
    // double-clicks a cell always lands in edit mode regardless of
    // the card's current selection state. Cheap insurance against the
    // card-handler swallowing a plain click in edge cases (stash of
    // pending pointer captures, mid-reorder, etc.).
    this._disposables.addFromEvent(this, 'dblclick', e => {
      if (this.view.readonly$.value) return;
      if (this.column.valueReadonly$.value) return;
      const sel = this.kanbanViewLogic.selectionController;
      if (!sel) return;
      e.stopPropagation();
      sel.selection = {
        selectionType: 'cell',
        groupKey: this.groupKey,
        rowKey: this.rowKey,
        cardId: this.cardId,
        columnId: this.column.id,
        isEditing: true,
      };
    });
  }

  isSelected(selection: KanbanViewSelection) {
    if (
      selection.selectionType !== 'cell' ||
      selection.groupKey !== this.groupKey
    ) {
      return;
    }
    return (
      selection.cardId === this.cardId && selection.columnId === this.column.id
    );
  }

  override render() {
    const props: CellRenderProps = {
      cell: this.column.cellGetOrCreate(this.cardId),
      isEditing$: this.isEditing$,
      selectCurrentCell: this.selectCurrentCell,
    };
    const renderer = this.column.renderer$.value;
    if (!renderer) return;
    const { view } = renderer;
    this.view.lockRows(this.isEditing$.value);
    this.dataset['editing'] = `${this.isEditing$.value}`;
    this.style.border = this.isFocus$.value
      ? '1px solid var(--streamlain-primary-color)'
      : '';
    this.style.boxShadow = this.isEditing$.value
      ? '0px 0px 0px 2px rgba(30, 150, 235, 0.30)'
      : '';
    const cell = renderUniLit(view, props, {
      ref: this._cell,
      class: 'kanban-cell',
      // `overflow: visible` on the cell container is intentional —
      // member avatars paint their focus/active ring via CSS
      // `outline`, which sits OUTSIDE the element's border-box. The
      // previous `overflow: hidden` amputated that ring at the cell
      // edge (see the member-cell fix at
      // database-block/.../member/style.css.ts, where the inner
      // `cellContainer` already keeps overflow visible for the same
      // reason). Text ellipsis is still enforced where it matters
      // — each property's own cell renderer caps its text column
      // with `overflow: hidden; text-overflow: ellipsis` — so
      // letting the outer wrapper stay visible doesn't let long
      // titles bleed across the card.
      style: {
        display: 'block',
        flex: '1 1 0',
        minWidth: '0',
        maxWidth: '100%',
        overflow: 'visible',
        width: '100%',
      },
    });
    if (this.contentOnly) {
      return html`${cell}`;
    }
    return html`${this.renderIcon()}
      <div class="kanban-cell-frame">
        <div class="kanban-cell-label" title="${this.column.name$.value}">
          ${this.column.name$.value}
        </div>
        ${cell}
      </div>`;
  }

  renderIcon() {
    if (this.contentOnly) {
      return;
    }
    // Readonly lock overlays the property-type icon bottom-right
    // instead of occupying its own column in the cell row. Table +
    // gantt headers still use the inline `renderReadonlyLock` helper
    // — kanban's narrow cards benefit from the badge form-factor.
    const lockBadge = this.column.readonly$.value
      ? html`<span
          class="icon-readonly-overlay"
          title="Read-only — value set by the system"
          aria-label="Read-only column"
          >${LockIcon()}</span
        >`
      : nothing;
    return html`
      <span class="icon-stack">
        <uni-lit class="icon" .uni="${this.column.icon}"></uni-lit>
        ${lockBadge}
      </span>
    `;
  }

  @property({ attribute: false })
  accessor cardId!: string;

  @property({ attribute: false })
  accessor column!: Property;

  @property({ attribute: false })
  accessor contentOnly = false;

  isEditing$ = signal(false);

  @property({ attribute: false })
  accessor groupKey!: string;

  @property({ attribute: false })
  accessor rowKey!: string;

  isFocus$ = signal(false);

  @property({ attribute: false })
  accessor kanbanViewLogic!: KanbanViewUILogic;

  get view() {
    return this.kanbanViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-kanban-cell': KanbanCell;
  }
}
